import React, { useContext, useState } from 'react'

import { DueOutFetchArgs } from '../../assets/ts/data/dueout/dueout'
import { STATUS_CODE } from '../../assets/ts/data/dueout/dueout-status-metadata'
import {
  COLUMN_NAMES as INQUIRY_COLUMN_NAMES,
  InquiryFetchArgs,
} from '../../assets/ts/data/inquiry/inquiry'
import { defaultToEmptyString, twoDigits } from '../../assets/ts/formatting/formatting'
import useDueOutCount from '../../hooks/dueout/use-dueout-count'
import useDueOutStatusCounts from '../../hooks/dueout/use-dueout-status-counts'
import useInquiryCount from '../../hooks/inquiry/use-inquiry-count'
import useInquiryStatusCounts from '../../hooks/inquiry/use-inquiry-status-counts'
import { RoleContext } from '../../store/role-context-provider'
import AccordionKPIs from './accordion-kpis/accordions-kpis'
import DetailKPIs from './detail-kpis/detail-kpis'
import './kpi.css'
import LargeKPI from './kpi-variants/large-kpi'
import SummaryKPIs from './summary-kpis/summary-kpis'

interface Props {
  filter?: DueOutFetchArgs | InquiryFetchArgs
  initialKpiFilters?: DueOutFetchArgs | InquiryFetchArgs
  isSavedDueoutDeleted?: boolean
  isSavedInquiryDeleted?: boolean
  onClearAll?: any
  pageType: PageType
  setKpiName?: any
  tab?: 'DUEOUTS' | 'INQUIRIES'
  updateKpiFilter: (e, tab) => void
}
interface CustomerActionRequiredCount {
  CustomerActionRequired: boolean
  InquiryGUID_count: number
}
const KPIs = ({
  filter,
  initialKpiFilters,
  isSavedDueoutDeleted,
  isSavedInquiryDeleted,
  onClearAll,
  pageType,
  setKpiName,
  tab,
  updateKpiFilter,
}: Props) => {
  const {
    isCPOwner,
    isCSS,
    isLeadership,
    isMAJCOMOwner,
    isMPFOwner,
    isRegAFOwner,
  } = useContext(RoleContext)
  const [now] = useState(new Date())

  const { dueOutCountValue: dueoutsTotalCount } = useDueOutCount({
    ...filter,
    ...initialKpiFilters,
  })
  const { inquiryCountValue: inquiriesTotalCount } = useInquiryCount({
    ...filter,
    ...initialKpiFilters,
    activeFilter: filter?.tabTable === 'ACTIVE' && filter?.activeFilter,
    assignedToMe: null,
    assignedToOther: null,
    currentSection: null,
    customerActionRequiredFilter: null,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: null,
    isResponsePending: undefined,
    isResponseRequired: undefined,
    needToWorkFilter: null,
    pendingActionOutsideFilter: null,
    returnedFromMPF: null,
    sectionCodeKPIFilter: undefined,
    statusCodeFilter: '',
    subCategoryCodeFilter: undefined,
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' && filter?.unassignedFilter,
  })
  const { inquiryCount: returnedCount } = useInquiryCount({
    ...filter,
    activeFilter: filter?.tabTable === 'ACTIVE' && filter?.activeFilter,
    assignedToMe: null,
    assignedToOther: null,
    currentSection: null,
    customerActionRequiredFilter: null,
    elevatedToMPF: null,
    isCPOwnerOrLeadership: null,
    isResponsePending: undefined,
    isResponseRequired: undefined,
    needToWorkFilter: null,
    pendingActionOutsideFilter: null,
    returnedFromMPF: true,
    sectionCodeKPIFilter: -1,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' && filter?.unassignedFilter,
  })
  const { inquiryCount: elevatedCount } = useInquiryCount({
    ...filter,
    activeFilter: filter?.tabTable === 'ACTIVE' && filter?.activeFilter,
    assignedToMe: null,
    assignedToOther: null,
    currentSection: null,
    customerActionRequiredFilter: null,
    elevatedToMPF: true,
    isCPOwnerOrLeadership: null,
    isResponsePending: undefined,
    isResponseRequired: undefined,
    needToWorkFilter: null,
    pendingActionOutsideFilter: null,
    returnedFromMPF: null,
    sectionCodeKPIFilter: -1,
    statusCodeFilter: '',
    unassignedFilter: filter?.tabTable === 'UNASSIGNED' && filter?.unassignedFilter,
  })

  const { inquiryStatusCounts: savedInquiryCount } = useInquiryStatusCounts({
    isArchivedFilter: false,
    isSavedInquiryDeleted: isSavedInquiryDeleted,
    isViewApplied: true,
    myInquiries: (filter as InquiryFetchArgs)?.myInquiries,
    savedFilter: true,
  })
  const { dueOutStatusCounts: savedDueoutCount } = useDueOutStatusCounts({
    isArchivedFilter: false,
    isSavedDueoutDeleted: isSavedDueoutDeleted,
    isViewApplied: true,
    myDueouts: (filter as DueOutFetchArgs)?.myDueouts,
    savedFilter: true,
  })
  const { dueOutCountValue: overdueDueOutCount } = useDueOutCount({
    ...filter,
    ...initialKpiFilters,
    dueDateLessThanDateIgnoreTime: now,
    statusCodesFilter: [STATUS_CODE.IN_PROGRESS, STATUS_CODE.NOT_STARTED],
  })
  const { inquiryCount: customerActionTotalCount } = useInquiryCount({
    ...initialKpiFilters,
    customerActionRequiredFilter: false,
    groupByArgs: [INQUIRY_COLUMN_NAMES.CustomerActionRequired],
    selectArgs: [INQUIRY_COLUMN_NAMES.InquiryGUID, INQUIRY_COLUMN_NAMES.CustomerActionRequired],
  })
  const actionRequiredCount = customerActionTotalCount
    .filter(
      (item): item is CustomerActionRequiredCount =>
        'CustomerActionRequired' in item && item.CustomerActionRequired === true
    )
    .map((item) => item.InquiryGUID_count)

  /** determineTotalsTitle:
   * Takes the page type and generates the Title for the Totals
   * example: pageType === 'INQUIRIES' -> 'TOTAL INQUIRIES'
   */
  const determineTotalsTitle = (
    pageType: PageType,
    tab: 'DUEOUTS' | 'INQUIRIES' | undefined
  ): 'TOTAL DUE OUTS' | 'TOTAL INQUIRIES' => {
    const title: 'TOTAL DUE OUTS' | 'TOTAL INQUIRIES' = `TOTAL ${
      pageType === 'INQUIRIES' ||
      pageType === 'MY INQUIRIES' ||
      (pageType === 'COMPLETED' && tab === 'INQUIRIES')
        ? 'INQUIRIES'
        : 'DUE OUTS'
    }`
    return title
  }
  /** showSummaryKPIs:
   * Takes the page type and determines a boolean which is used to display the Summary KPIs.
   * example: pageType === 'INQUIRIES' -> 'TOTAL INQUIRIES'
   */
  const showSummaryKPIs = (pageType: PageType): boolean => {
    let isVisible = false
    const VisiblePages = ['MY DUEOUTS', 'MY INQUIRIES', 'DUE OUTS', 'COMPLETED']
    if (VisiblePages.includes(pageType)) {
      isVisible = true
    } else {
      isVisible = false
    }
    return isVisible
  }
  const renderSavedKPI = (count: number) => (
    <div className='col col-md-12 align-items-stretch d-flex mt-1'>
      <div className='bordered2 border-radius w-100 p-0'>
        <LargeKPI
          kpi={{
            colorcode: 22,
            count: twoDigits(count),
            link: { savedFilter: true },
            pageType: pageType,
            title: 'Saved',
          }}
          setKpiName={setKpiName}
          updateKpiFilter={updateKpiFilter}
        />
      </div>
    </div>
  )

  const savedDueoutCountValue =
    savedDueoutCount.find((item) => item.StatusCode === STATUS_CODE.SAVED)?.DueOutGUID_count ?? 0

  const savedInquiryCountValue =
    savedInquiryCount.find((item) => item.StatusCode === STATUS_CODE.SAVED)?.InquiryGUID_count ?? 0

  return (
    <>
      <div className='bgcolor-13 border-radius1 overflow-hidden m-2 d-flex'>
        <div
          aria-describedby='kpisheader'
          className='d-flex align-items-center poppins-bold w-100 cursor'
          id='kpisheader'
          onClick={onClearAll}
          tabIndex={0}
          title='TOTAL INQUIRIES'
        >
          <h3 className='m-0 me-2 px-2 ps-3 py-1' title={determineTotalsTitle(pageType, tab)}>
            <p className='whitetext font-14'>{determineTotalsTitle(pageType, tab)}</p>
          </h3>
          <h3 className='ms-auto align-items-center p-3 px-4 d-flex h-100 bgcolor-30 whitetext font-25'>
            {(pageType === 'COMPLETED' && tab === 'INQUIRIES') ||
            pageType === 'MY INQUIRIES' ||
            pageType === 'INQUIRIES'
              ? defaultToEmptyString(inquiriesTotalCount)
              : defaultToEmptyString(dueoutsTotalCount)}
          </h3>
        </div>
      </div>
      <div>
        {pageType === 'INQUIRIES' &&
          (isCSS ? (
            <DetailKPIs
              allCount={inquiriesTotalCount.toString()}
              filter={filter}
              kpiTitle={'subkpis1'}
              sectionCode={1}
              setKpiName={setKpiName}
              updateKpiFilter={updateKpiFilter}
            />
          ) : (
            <AccordionKPIs
              filter={filter}
              setKpiName={setKpiName}
              updateKpiFilter={updateKpiFilter}
            />
          ))}
      </div>
      <div className='row w-100 m-0 px-1'>
        {pageType === 'INQUIRIES' &&
          (isCPOwner ||
            isMPFOwner ||
            isLeadership ||
            isRegAFOwner ||
            isMAJCOMOwner||
            isCSS
            ) && (
            <>
              <div className='col col-md-12 align-items-stretch d-flex mt-1'>
                <div className='bordered2 border-radius w-100 p-0'>
                  <LargeKPI
                    kpi={{
                      colorcode: 18,
                      count: returnedCount[0]?.InquiryGUID_count
                        ? returnedCount[0]?.InquiryGUID_count
                        : '00',
                      title: 'Returned From Mpf',
                    }}
                    setKpiName={setKpiName}
                    updateKpiFilter={updateKpiFilter}
                  />
                </div>
              </div>
              <div className='col col-md-12 align-items-stretch d-flex mt-1'>
                <div className='bordered2 border-radius w-100 p-0'>
                  <LargeKPI
                    kpi={{
                      colorcode: 19,
                      count: elevatedCount[0]?.InquiryGUID_count
                        ? elevatedCount[0]?.InquiryGUID_count
                        : '00',
                      title: 'Elevated To Mpf',
                    }}
                    setKpiName={setKpiName}
                    updateKpiFilter={updateKpiFilter}
                  />
                </div>
              </div>
            </>
          )}
      </div>
      {showSummaryKPIs(pageType) && (
        <div className='row flex-wrap px-2'>
          <SummaryKPIs
            filter={filter}
            initialKpiFilters={initialKpiFilters}
            pageType={pageType}
            setKpiName={setKpiName}
            tab={tab}
            totalCount={tab === 'INQUIRIES' ? inquiriesTotalCount : dueoutsTotalCount}
            updateKpiFilter={updateKpiFilter}
          />
        </div>
      )}
      <div className='row w-100 m-0 px-1'>
        {pageType === 'MY INQUIRIES' && (
          <div className='col col-md-12 align-items-stretch d-flex mt-1'>
            <div className='bordered2 border-radius w-100 p-0'>
              <LargeKPI
                kpi={{
                  colorcode: 21,
                  count: twoDigits(actionRequiredCount[0] ?? 0),
                  link: { customerActionRequiredFilter: true },
                  pageType: pageType,
                  title: 'Customer Action Required',
                }}
                setKpiName={setKpiName}
                updateKpiFilter={updateKpiFilter}
              />
            </div>
          </div>
        )}
        {(pageType === 'DUE OUTS' || pageType === 'MY DUEOUTS') && (
          <div className='col col-md-12 align-items-stretch d-flex mt-1'>
            <div className='bordered2 border-radius w-100 p-0'>
              <LargeKPI
                kpi={{
                  colorcode: 21,
                  count: twoDigits(overdueDueOutCount ?? 0),
                  link: {
                    dueDateLessThanDateIgnoreTime: now,
                    statusCodesFilter: [STATUS_CODE.IN_PROGRESS, STATUS_CODE.NOT_STARTED],
                  },
                  pageType: pageType,
                  title: 'OverDue',
                }}
                setKpiName={setKpiName}
                updateKpiFilter={updateKpiFilter}
              />
            </div>
          </div>
        )}
        {pageType === 'MY DUEOUTS' && renderSavedKPI(savedDueoutCountValue)}
        {pageType === 'MY INQUIRIES' && renderSavedKPI(savedInquiryCountValue)}
      </div>
    </>
  )
}
export type PageType = 'COMPLETED' | 'DUE OUTS' | 'INQUIRIES' | 'MY DUEOUTS' | 'MY INQUIRIES'
export default KPIs
